import React, { ReactNode, useContext, useState } from "react";
import { RulesEditorProviderValue, RulesEditorContext } from ".";
import { Question, Section } from "interfaces/Phoenix.FormsDesigner.Models";
import PNXLabel from "components/PhoenixFormsV4/Controls/PNXLabel";
import { classes } from "components/Utility";
import IconButton from "components/Shared/IconButton";
import { MSelect } from "components/Shared";
import { stopPropagationPreventDefault, stopPropagationPreventDefaultNoFn } from "tools/clickEvents";
import { RadioGroup, Radio, FormControlLabel, FormGroup, Checkbox, TextField } from "@mui/material";
import { Button } from "components/Shared";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";

interface RulesQuestionProps {
  className?: string;
  question: Question;
  parentSection?: Section;
  parentQuestion?: Question;
}

export const reorderQuestionHandle = "reorder-question-handle";
export const reorderNestedQuestionHandle = "reorder-nested-question-handle";

const RulesEditorFormControl = ({ className, question, parentSection, parentQuestion }: RulesQuestionProps) => {
  const context = useContext<RulesEditorProviderValue>(RulesEditorContext);
  const [isGroupRepeatCollapsed, setIsGroupRepeatCollapsed] = useState<boolean>(false);

  let controlPreview: ReactNode;
  let isGroupOrRepeat = false;
  let secondaryControlPreview: ReactNode = "";

  let labelValEnumeration = question.control.dataField?.enumeration?.items?.map(i => ({ label: <PNXLabel>{i.name}</PNXLabel> as any as string, value: i.value }));
  if (!labelValEnumeration?.length) labelValEnumeration = [{ label: "Sample Item", value: "" }];

  switch (question.control.type) {
    case "DynamicItemsetSelectOne":
    case "AdvancedDynamicItemsetSelectOne":
    case "SelectOne":
      switch (question.control.appearance) {
        case "compact":
          controlPreview = (
            <MSelect
              placeholder="Select..."
              isClearable
              options={labelValEnumeration}
            />
          );
          break;
        case "phoenix:combo":
          controlPreview = (
            <MSelect
              placeholder="Search or Enter Value..."
              isClearable
              creatable
              options={labelValEnumeration}
            />
          );
          break;
        case "minimal":
          controlPreview = (
            <MSelect
              placeholder="Search or Enter Value..."
              isClearable
              options={labelValEnumeration}
            />
          );
          break;
        case "full":
          controlPreview = (
            <RadioGroup>
              {labelValEnumeration.map((item, idx) => (
                <FormControlLabel key={idx} value={item.value} control={<Radio />} label={item.label} />
              ))}
            </RadioGroup>
          );
      }
      break;
    case "SelectMany":
    case "SelectGroup":
      controlPreview = (
        <FormGroup>
          {labelValEnumeration.map((item, idx) => (
            <FormControlLabel key={idx} control={<Checkbox />} label={item.label} />
          ))}
        </FormGroup>
      );
      break;
    case "TextBox":
      controlPreview = (
        <TextField
          disabled
          value="Sample Text"
          placeholder="Enter Value..."
          variant="standard"
        />
      );
      break;
    case "Label":
      controlPreview = null;
      break;
    case "Group":
    case "Repeat":
      isGroupOrRepeat = true;

      let controls = question.control.controls.reduce((acc, c) => {
        if (c.type === "QuestionReference") {
          let innerQuestion = context.getQuestionById(c.questionId);

          acc.push({
            id: c.questionId,
            node: <RulesEditorFormControl
                    key={c.questionId}
                    question={innerQuestion}
                    parentQuestion={question}
                  />,
          });
        }
        return acc;
      }, [] as ReorderableItem[]);

      if (question.control.type === "Repeat") {
        if (question.control.appearance === "compact") secondaryControlPreview = "Collapsible Repeat";
        else if (question.control.appearance === "static") secondaryControlPreview = "Readonly Repeat";
        else secondaryControlPreview = "Repeat";
      }

      if (question.control.type === "Group") {
        secondaryControlPreview = "Group";
      }

      controlPreview = question.control.controls.length === 0 ?
        <Button
          primary
          onClick={() => context.editFns.addNewQuestion({ repeatOrGroup: question })}
          className="add-question-to-group"
        >
          Add New Rule
        </Button>
        :
        <ReorderableGrid
          className="section-controls repeat"
          itemClassName={question.control.type === "Group" && "group-reorder"}
          items={controls}
          handleSelector={`.${reorderQuestionHandle}`}
          onReorder={ids => context.editFns.reorderQuestions({ repeatOrGroup: question }, ids)}
        />;
      break;
    case "Photo":
      controlPreview = <IconButton icon="photo_camera" />;
      break;
    case "Calculate":
      controlPreview = <IconButton icon="calculate" />;
      break;
    case "Ruleset":
    case "Recommendation":
      controlPreview = <IconButton icon="square_foot" />;
      break;
    case "File":
      controlPreview = <IconButton icon="attach_file" />;
      break;
  }

  const divClassName =
    classes(
      "designer-question formcontrol",
      className,
      context?.selectedRule?.id === question.id && "selected-question",
      question.control.type === "Group" && "group-control",
      question.control.type === "Repeat" && "repeat-control"
    );

  const controlWithoutPreview: ReactNode[] = [
    isGroupOrRepeat && <IconButton icon="keyboard_arrow_down" className={classes("md-collapser", !isGroupRepeatCollapsed && "md-collapser--flipped")} onClick={stopPropagationPreventDefault(() => setIsGroupRepeatCollapsed(!isGroupRepeatCollapsed))}/>,
    <IconButton icon="reorder" className={reorderQuestionHandle} onClick={stopPropagationPreventDefaultNoFn} />,
    <IconButton icon="add" onClick={stopPropagationPreventDefault(() => context.editFns.addNewQuestion({ modifierQuestionId: question.id, section: parentSection, repeatOrGroup: parentQuestion }))} />,
    !isGroupOrRepeat && <IconButton icon="content_copy" onClick={stopPropagationPreventDefault(() => context.clipboard.copy(question.id))} />,
    <IconButton icon="delete" onClick={stopPropagationPreventDefault(() => context.editFns.deleteQuestion(question.id))} />,
    <div className="designer-control-label"><PNXLabel>{question.control.label}</PNXLabel></div>,
  ].filter(Boolean);

  return (
    <div className={divClassName} onClick={stopPropagationPreventDefault(() => context.setSelectedRule(question))}>
      {isGroupOrRepeat ?
      <>
        <div className="control-title">{controlWithoutPreview}{secondaryControlPreview}{isGroupRepeatCollapsed}</div>
        {!isGroupRepeatCollapsed && <div className="control-preview">{controlPreview}</div>}
      </>
      :
      <>
        {controlWithoutPreview}
        {<div className="control-preview">{controlPreview}</div>}
      </>}
    </div>
  );
};

export default RulesEditorFormControl;
